import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface NetworkStatusState {
  isOffline: boolean;
  failedOperations: Set<string>;
  lastFailureTime: number | null;
}

interface NetworkStatusContextValue {
  isOffline: boolean;
  retry: () => void;
  onOperationStart: (operationName: string) => void;
  onOperationSuccess: (operationName: string) => void;
  onOperationError: (operationName: string, error: any) => void;
}

const NetworkStatusContext = createContext<NetworkStatusContextValue | null>(
  null
);

const FAILURE_THRESHOLD = 3;
const RECOVERY_TIMEOUT = 5000; // 5 seconds

function isNetworkRelatedError(error: any): boolean {
  if (!error) return false;

  const errorMessage = error.message || "";
  const networkError = error.networkError;

  return (
    !!networkError ||
    errorMessage.includes("timeout") ||
    errorMessage.includes("fetch") ||
    errorMessage.includes("Network") ||
    errorMessage.includes("Failed to fetch") ||
    errorMessage.includes("CORS") ||
    (networkError && networkError.statusCode >= 500)
  );
}

export const NetworkStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<NetworkStatusState>({
    isOffline: false,
    failedOperations: new Set(),
    lastFailureTime: null,
  });

  const pendingOperations = useRef(new Set<string>());
  const recoveryTimeoutRef = useRef<NodeJS.Timeout>();

  const updateNetworkStatus = useCallback((offline: boolean) => {
    setState((prev) => {
      if (prev.isOffline !== offline) {
        // Dispatch event for components that need it
        window.dispatchEvent(
          new CustomEvent("networkStatusChange", {
            detail: { offline },
          })
        );

        if (!offline) {
          // Clear failed operations when back online
          return {
            ...prev,
            isOffline: false,
            failedOperations: new Set(),
            lastFailureTime: null,
          };
        }
      }
      return { ...prev, isOffline: offline };
    });
  }, []);

  const onOperationStart = useCallback((operationName: string) => {
    pendingOperations.current.add(operationName);
  }, []);

  const onOperationSuccess = useCallback(
    (operationName: string) => {
      pendingOperations.current.delete(operationName);

      setState((prev) => {
        const newFailedOperations = new Set(prev.failedOperations);
        newFailedOperations.delete(operationName);

        // If we had failures but now have success, we might be recovering
        if (
          prev.isOffline &&
          pendingOperations.current.size === 0 &&
          newFailedOperations.size === 0
        ) {
          console.log("Operations succeeding - network appears to be back");
          updateNetworkStatus(false);
        }

        return {
          ...prev,
          failedOperations: newFailedOperations,
        };
      });
    },
    [updateNetworkStatus]
  );

  const onOperationError = useCallback(
    (operationName: string, error: any) => {
      pendingOperations.current.delete(operationName);

      if (!isNetworkRelatedError(error)) {
        return; // Not a network error, ignore
      }

      setState((prev) => {
        const newFailedOperations = new Set(prev.failedOperations);
        newFailedOperations.add(operationName);

        const now = Date.now();
        console.log(
          `Network error in ${operationName}, total failures: ${newFailedOperations.size}`
        );

        // If we have multiple network failures, consider ourselves offline
        if (newFailedOperations.size >= FAILURE_THRESHOLD) {
          console.log(
            "Multiple network failures detected - marking as offline"
          );
          updateNetworkStatus(true);
        }

        return {
          ...prev,
          failedOperations: newFailedOperations,
          lastFailureTime: now,
        };
      });
    },
    [updateNetworkStatus]
  );

  const retry = useCallback(() => {
    // Force a page reload to retry all queries
    window.location.reload();
  }, []);

  // Listen to browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      console.log("Browser detected: back online");
      updateNetworkStatus(false);
    };

    const handleOffline = () => {
      console.log("Browser detected: offline");
      updateNetworkStatus(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [updateNetworkStatus]);

  // Auto-recovery mechanism
  useEffect(() => {
    if (state.isOffline && state.lastFailureTime) {
      // Clear any existing timeout
      if (recoveryTimeoutRef.current) {
        clearTimeout(recoveryTimeoutRef.current);
      }

      // Set a timeout to attempt recovery
      recoveryTimeoutRef.current = setTimeout(() => {
        console.log("Attempting auto-recovery from offline state");
        setState((prev) => ({
          ...prev,
          failedOperations: new Set(),
        }));
        updateNetworkStatus(false);
      }, RECOVERY_TIMEOUT);
    }

    return () => {
      if (recoveryTimeoutRef.current) {
        clearTimeout(recoveryTimeoutRef.current);
      }
    };
  }, [state.isOffline, state.lastFailureTime, updateNetworkStatus]);

  const contextValue: NetworkStatusContextValue = {
    isOffline: state.isOffline,
    retry,
    onOperationStart,
    onOperationSuccess,
    onOperationError,
  };

  return (
    <NetworkStatusContext.Provider value={contextValue}>
      {children}
    </NetworkStatusContext.Provider>
  );
};

export const useNetworkStatus = () => {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error(
      "useNetworkStatus must be used within a NetworkStatusProvider"
    );
  }
  return context;
};
